/**
 * CategoryIcon — 카테고리 아이콘 공용 컴포넌트
 *
 * DB icon_url(PNG) 우선, 없으면 lucide fallback. lucide 는 PNG 와 동일한 시각 (흰 카드)
 * 위해 흰 둥근 카드(rounded-md + bg-white + shadow-sm) 안에 표시.
 *
 * size 별 카드 크기 + 아이콘 크기 매핑은 본 파일에서만 정의 — 모든 사용처에서 동일.
 *
 * 사용처: LeftSidebar / MainMenu(모바일 그리드) / RightSidebar 등 카테고리 표시 전부.
 */

import { getCategoryIcon, getCategoryIconImageSrc } from "../../constants/categoryStyles";

export type CategoryIconSize = "sm" | "md" | "lg";

interface CategoryIconProps {
  cat: { key: string; icon_url?: string | null };
  size?: CategoryIconSize;
  className?: string;
}

// PNG 카드는 자체 디자인 그대로(흰 카드 + 컬러 아이콘). lucide 는 wrapper 로 흰 카드 모사.
// lucide 아이콘 자체 크기는 PNG 안 시각 아이콘과 동일하게 보이도록 카드의 약 55~60% 로 설정.
const SIZES: Record<
  CategoryIconSize,
  { card: string; lucide: string }
> = {
  sm: { card: "h-5 w-5", lucide: "h-3 w-3" },
  md: { card: "h-7 w-7", lucide: "h-4 w-4" },
  lg: { card: "h-16 w-16", lucide: "h-9 w-9" },
};

export default function CategoryIcon({
  cat,
  size = "md",
  className = "",
}: CategoryIconProps) {
  const { card, lucide } = SIZES[size];

  // 1순위: DB icon_url(3D PNG). 2순위: 로컬 3D PNG fallback(icon_url 미설정 카테고리 — 홀덤/차량 등).
  const imgSrc = cat.icon_url || getCategoryIconImageSrc(cat.key);
  if (imgSrc) {
    return (
      <img
        src={imgSrc}
        alt=""
        className={`${card} object-contain shrink-0 ${className}`}
      />
    );
  }

  // 3순위: lucide 선 아이콘 (3D PNG 둘 다 없을 때만)
  const Icon = getCategoryIcon(cat.key);
  return (
    <span
      // text-slate-600 명시 — 부모 button hover/active 의 currentColor 차단.
      // PNG(img) 가 raster 라 색 변화 없는 것과 동일하게 lucide 도 색 고정 → 모든 카테고리 시각 통일
      className={`flex ${card} items-center justify-center rounded-md bg-white shadow-sm shrink-0 text-slate-600 ${className}`}
    >
      <Icon className={lucide} strokeWidth={2.2} />
    </span>
  );
}
